import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    output,
    signal,
    WritableSignal,
} from '@angular/core';
import { CtrlModal, CtrlModalComponent } from '@co-3/ctrl-ui/modals';
import { FileWithMeta } from '../../models';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CtrlAutoresizeTextareaDirective } from '@co-3/ctrl-ui/forms';
import { CreateDamageReportService } from '../../create-damage-report.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, switchMap } from 'rxjs';
import { Contract } from '@models/contract';
import { PortalActionButtonsComponent } from 'app/customer-portal/components';
import { DamageDocumentTypesService } from 'app/customer-portal/damage-reports/damage-document-types.service';
import { imgPreview } from 'app/utils/img-preview';
import { RequiredInfoComponent } from 'app/shared/components/required-info/required-info.component';

@Component({
    selector: 'app-file-attachment-meta-modal',
    imports: [
        CtrlModalComponent,
        CtrlIconComponent,
        CtrlAutoresizeTextareaDirective,
        ReactiveFormsModule,
        RequiredInfoComponent,
        PortalActionButtonsComponent,
    ],
    templateUrl: './file-attachment-meta-modal.component.html',
    styleUrl: './file-attachment-meta-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileAttachmentMetaModalComponent implements CtrlModal<FileWithMeta> {
    public file = input<FileWithMeta>();
    public currentFileNo = input<number>();
    public totalFileNo = input<number>();

    public dismiss = output<FileWithMeta>();

    private damageDocumentTypesService = inject(DamageDocumentTypesService);
    private createDamageReportService = inject(CreateDamageReportService);

    protected imgSrc: WritableSignal<string | ArrayBuffer | null> = signal(null);
    public docTypeOptions = toSignal(
        this.createDamageReportService.selectedContract$.pipe(
            takeUntilDestroyed(),
            filter((contract: Contract | null) => !!contract),
            switchMap((contract: Contract) =>
                this.damageDocumentTypesService.getDocumentTypes(contract.insuranceType.id),
            ),
        ),
    );

    public form = new FormGroup({
        documentTypeId: new FormControl<number>(null, Validators.required),
        description: new FormControl<string>(''),
    });

    public constructor() {
        effect(() => {
            if (!this.file() || this.file().file.type === 'application/pdf') {
                this.imgSrc.set(null);
            } else {
                imgPreview(this.file().file).then((result: string | ArrayBuffer | null) => this.imgSrc.set(result));
            }
            if (this.file()) {
                this.form.patchValue({
                    documentTypeId: this.file().documentTypeId ?? null,
                    description: this.file().description ?? '',
                });
            }
        });
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            return;
        }
        this.dismiss.emit({
            ...this.form.getRawValue(),
            file: this.file().file,
        });
    }
}
